


















































































import {
  defineComponent,
  onMounted,
  ref,
  computed,
  inject,
  reactive,
  watchEffect,
  Ref
} from '@vue/composition-api'
import KaiHeader from './components/layout/KaiHeader.vue'
import KaiConversation from './components/transcript/KaiConversation.vue'
import SelectedTagsList from './components/layout/SelectedTagsList.vue'
import AllTagsSection from './components/layout/AllTagsSection.vue'
import { useActions } from './store/useActions'
import { useGetters } from './store/useGetters'
import { UITag } from './types/tag'
import {
  getBrowser,
  mapSpeakers,
  mapTagsToUI,
  topicTagsToApi,
  topicTagToApi,
  transcriptWordToUI
} from './utils'
import {
  TopicTag,
  Transcript,
  TranscriptionV2ApiKey
} from './transcription.api'
import { useIndexedDb } from './composables/useIndexedDb'
import { ConversationsApiKey } from '@/rest/conversations-api'
import { noticeablyAsync } from '@/lib/async'
import { Success, UnwrappedResponse } from './api/responses'
import { MediaCdnKey } from '@/lib/media-cdn'
import { SavingStep, SavingSteps } from './types/transcript'
import { UISpeaker, Word } from './models/word'

export default defineComponent({
  name: 'App',
  components: {
    KaiHeader,
    SelectedTagsList,
    KaiConversation,
    AllTagsSection
  },
  props: {
    transcriptionId: Number
  },
  setup(props) {
    const loading = ref(null)
    const transcriptVersion = ref<number | undefined>()
    const transcriptionApi = inject(TranscriptionV2ApiKey)
    const conversationsApi = inject(ConversationsApiKey)
    const mediaCdn = inject(MediaCdnKey)
    const transcriptRestoredFromDb = ref<boolean>(false)
    const client = ref('')
    const owner = ref('')
    const title = ref('')
    const loadingLabel = ref('')
    const language = ref('')
    const command = reactive({
      promise: null,
      label: '',
      success: '',
      failure: ''
    })
    const saving: Ref<SavingStep> = ref({
      waiting: true,
      working: false,
      promise: null,
      label: '',
      success: '',
      failure: '',
      attempt: 0
    })
    const steps: Ref<SavingStep[]> = ref([])

    const setSaving = (step: SavingStep) => {
      saving.value = step
      saving.value.working = true
      saving.value.payload = {
        id: props.transcriptionId,
        version: getters.transcript.value.version
      }
      saving.value.promise = step.method(saving.value.payload)
      saving.value.promise.then(stepResolver)
    }

    const stepResolver = ({
      data: transcript
    }: Success<Partial<Transcript>>) => {
      if (saving.value.step >= saving.value.steps) {
        saving.value.isResolved = true
      } else {
        const ind = saving.value.step - 1
        steps.value[ind].payload = transcript
        steps.value[ind].promise = steps.value[ind].method(
          steps.value[ind].payload
        )
        steps.value[ind].retryMethod = () => {
          steps.value[ind].failure = `Retry attempt ${++steps.value[ind]
            .attempt}`
          steps.value[ind].promise = steps.value[ind].method(
            steps.value[ind].payload
          )
          steps.value[ind].waiting = false
          steps.value[ind].working = true
          ;(steps.value[ind].promise as Promise<Success<Transcript>>).then(
            stepResolver
          )
        }
        steps.value[ind].waiting = false
        steps.value[ind].working = true
        ;(steps.value[ind].promise as Promise<Success<Transcript>>).then(
          stepResolver
        )
        saving.value.step = saving.value.step + 1
      }
      return transcript
    }

    const onSaving = (promise: SavingSteps) => {
      setSaving(promise.current)
      saving.value.steps = promise.steps.length + 1
      steps.value = []
      promise.steps.forEach((stepPromise: SavingStep) => {
        stepPromise.retryMethod = () => {}
        steps.value.push(stepPromise)
      })
    }

    const onSwapping = (promise: Promise<unknown>) => {
      saving.value.promise = promise
      saving.value.working = true
      saving.value.label = 'Swapping Speakers...'
      saving.value.success = 'Speakers Swapped'
      saving.value.failure = 'Swapping Speakers failed'
    }

    const reload = () => window.location.reload()

    const actions = useActions({
      initTranscript: 'conversation/initTranscript',
      restoreTranscript: 'conversation/restoreTranscript',
      initTopics: 'tagsPicker/initTopics',
      initTopicTags: 'transcriptTags/initTopicTags',
      setBrowser: 'conversation/setBrowser',
      updateTags: 'transcriptTags/updateTags',
      toggleIsEditable: 'conversation/toggleIsEditable'
    })

    const getters = useGetters({
      transcriptWordsToApi: 'conversation/transcriptWordsToApi',
      initialTranscript: 'conversation/originalTranscript',
      colorPalette: 'tagsPicker/topicsColors',
      topicTags: 'transcriptTags/topicTags',
      deletedTags: 'transcriptTags/deletedTags',
      transcript: 'conversation/transcript',
      isEditable: 'conversation/isEditable'
    })

    const { initDB, getTranscript } = useIndexedDb()

    async function init(transcription: any, tags: any) {
      actions.initTranscript(transcription.transcript)

      const topicTagsWithTopicId = tags.map((t: TopicTag) => ({
        ...t,
        topicId: t.type + t.category + t.subCategory
      }))

      const [topicsPalette, analysisPalette] = await actions.initTopics(
        transcription.topics
      )
      actions.initTopicTags({
        tags: topicTagsWithTopicId,
        words: getters.transcript.value.words,
        topicsPalette,
        analysisPalette
      })
    }

    onMounted(async () => {
      //forceReadModeOnFirstArriving()
      actions.setBrowser(getBrowser())
      preventFromScrollingOnSpaceKeyPress()
      if (props.transcriptionId) {
        loading.value = refetchData()
      }
    })

    function upvoteTag(tag: UITag) {
      console.log('Upvote...')
      command.label = 'Saving tag upvote'
      command.success = 'Tag upvote saved'
      command.failure = 'Tag upvote failed'
      const upvoteTag = noticeablyAsync(() =>
        transcriptionApi.upvoteTag(tag.transcriptId, tag.id)
      )
      command.promise = upvoteTag()
    }

    function downvoteTag(tag: UITag) {
      command.label = 'Saving tag downvote'
      command.success = 'Tag downvote saved'
      command.failure = 'Tag downvote failed'
      const downvoteTag = noticeablyAsync(() =>
        transcriptionApi.downvoteTag(tag.transcriptId, tag.id)
      )
      command.promise = downvoteTag()
    }

    function updateTag(
      tag: UITag,
      note: string,
      subCategory: string,
      deleted?: boolean
    ) {
      command.label = 'Saving tag subcategory to note'
      command.success = 'Tag note updated'
      command.failure = 'Tag note update failed'

      const updateTag = noticeablyAsync(() =>
        transcriptionApi.updateTag(
          props.transcriptionId,
          tag.id,
          note,
          subCategory,
          false,
          deleted
        )
      )
      command.promise = updateTag().catch(e => console.error(e))
    }

    const mapTimeToWord = (transcript: Transcript) => {
      const speakers: UISpeaker[] = mapSpeakers(transcript)

      return transcriptWordToUI(transcript, speakers)
    }

    const findWords = (words: Word[], startTime: number, endTime: number) => {
      const found = words.filter(
        word => word.start === startTime || word.end === endTime
      )
      found.sort((a, b) => a.index - b.index)
      if (found.length < 1) return [0, 0]
      return [found[0].index, found[found.length - 1].index]
    }

    async function refetchData() {
      return Promise.all([
        conversationsApi.GetConversation(props.transcriptionId),
        transcriptionApi.loadTranscript(props.transcriptionId),
        transcriptionApi.loadTags(props.transcriptionId)
      ]).then(([conversation, transcription, tags]) => {
        if (
          conversation._tag === 'success' &&
          transcription._tag === 'success' &&
          tags._tag === 'success'
        ) {
          client.value = conversation.data[0].client_display_name
          owner.value = conversation.data[0].owner_display_name
          title.value = conversation.data[0].title
          language.value = conversation.data[0].language
          transcriptVersion.value = transcription.data.transcript.version
          loadingLabel.value = 'Initializing transcript'
          const words: any[] = mapTimeToWord(transcription.data.transcript)
          tags.data.forEach(tag => {
            if (!tag.startWordIndex) {
              const [startWordIndex, endWordIndex] = findWords(
                words,
                tag.startTime,
                tag.endTime
              )
              tag.startWordIndex = startWordIndex
              tag.endWordIndex = endWordIndex
            }
          })
          return init(transcription.data, tags.data)
        } else if (conversation._tag === 'failure') {
          console.error(conversation.error)
        } else if (transcription._tag === 'failure') {
          console.error(transcription.error)
        } else if (tags._tag === 'failure') {
          console.error(tags.error)
        }
      })
    }

    async function deleteTag(tag: UITag) {
      command.label = 'Deleting tag'
      command.success = 'Tag deleted'
      command.failure = 'Tag delete failed'
      const deleteTag = noticeablyAsync(() =>
        transcriptionApi.deleteTag(tag.transcriptId, tag.id)
      )
      command.promise = deleteTag().catch(e => console.error(e))
    }

    const tagToApiResource = (tag: TopicTag): TopicTag => ({
      ...tag,
      createdBy: '<?>',
      editedAt: tag.createdAt,
      editedBy: '<?>',
      note: '',
      transcriptId: props.transcriptionId,
      transcriptVersion: transcriptVersion.value
    })

    async function addTags(data: {
      tagsToAdd: UITag[]
      tagsToDelete: UITag[]
    }) {
      // There are cases when adding a tag requires deleting other tags (when tags with same topic id are intersected)
      const tags: TopicTag[] = topicTagsToApi(data.tagsToAdd)

      function successful<T>(
        apiResponse: UnwrappedResponse<T>
      ): apiResponse is Success<T> {
        return apiResponse._tag === 'success'
      }

      console.log({ tagsToDelete: data.tagsToDelete })

      command.label = 'Saving tag'
      command.success = 'Tag saved'
      command.failure = 'Saving tag failed'
      const savingTags = Promise.all(
        data.tagsToDelete.map(tagToDelete =>
          transcriptionApi.deleteTag(props.transcriptionId, tagToDelete.id)
        )
      )
        .then(() =>
          Promise.all(
            tags
              .map(tagToApiResource)
              .map(tag => transcriptionApi.addTag(props.transcriptionId, tag))
          )
        )
        .then(responses =>
          responses.filter(successful).map(success => success.data)
        )

      command.promise = savingTags
      const responseTags = await savingTags

      // map response tags to UI
      const newTags = mapTagsToUI(responseTags as any, true)

      // update existing tags with ones that came from API
      actions.updateTags({ prevTags: data.tagsToAdd, newTags })
    }

    async function editTag(data: {
      tagsToEdit: UITag[]
      tagsToDelete: UITag[]
    }) {
      // There are cases when editing a tag requires deleting other tags (when tags with same topic id are intersected)
      const tag: TopicTag = topicTagToApi(data.tagsToEdit[0])

      command.label = 'Saving tag'
      command.success = 'Tag saved'
      command.failure = 'Saving tag failed'
      command.promise = Promise.all(
        data.tagsToDelete.map(tagToDelete =>
          transcriptionApi.deleteTag(props.transcriptionId, tagToDelete.apiId)
        )
      ).then(() =>
        transcriptionApi.addTag(props.transcriptionId, tagToApiResource(tag))
      )

      await command.promise

      // make a request
      const responseTag = tag
      // map response tags to UI
      const newTags = mapTagsToUI([responseTag], true)

      // update existing tags
      actions.updateTags({ prevTags: [data.tagsToEdit[0]], newTags })
    }

    function preventFromScrollingOnSpaceKeyPress() {
      window.addEventListener('keydown', function(e) {
        if (e.key === ' ' && e.target == document.body) {
          e.preventDefault()
        }
      })
    }

    function undoLastEditChange() {
      //
    }

    /**
     * Get transcript from local storage if present
     */
    async function restoreTranscript() {
      const db = await initDB()
      if (!db.value) {
        transcriptRestoredFromDb.value = true
        return
      }

      const transcript = await getTranscript(db)

      transcriptRestoredFromDb.value = true
      if (!transcript) {
        return
      }
      actions.restoreTranscript(transcript)
    }

    const audioUrl = ref('')
    watchEffect(
      async () =>
        (audioUrl.value = await mediaCdn.elements.Audio(props.transcriptionId))
    )

    function edit(work) {
      command.label = 'Entering edit mode'
      command.promise = work
    }

    function cancelling(work) {
      command.label = 'Leaving edit mode'
      command.promise = work
    }

    const transcriptHeading = computed(() => `${client.value} - ${owner.value}`)
    const transcriptTitle = computed(() => title.value)
    const transcriptLanguage = computed(() => language.value)

    return {
      transcript: getters.transcript,
      loading,
      saving,
      steps,
      edit,
      cancelling,
      reload,
      onSaving,
      onSwapping,
      transcriptRestoredFromDb,
      upvoteTag,
      downvoteTag,
      updateTag,
      addTags,
      editTag,
      deleteTag,
      audioUrl,
      transcriptHeading,
      transcriptTitle,
      transcriptLanguage,
      command,
      undoLastEditChange
    }
  }
})
